'use client';

import React, { useState, useEffect } from 'react';
import { Avatar, Input, message } from 'antd';
import { RightOutlined, ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { getClients } from '../../../services/api';

interface ClientData {
    svc_cat_id: number;
    clt_corporation_details: {
        id: number;
        full_name: string;
        logo_url: string | null;
    };
    svc_cat_details: {
        id: number;
        display: string;
        logo_url: string | null;
    };
    clt_legal_entity_details: {
        id: number;
        name: string;
        logo_url: string | null;
    };
}

interface ClientSelectionProps {
    serviceName: string;
    onBack: () => void;
    onClientSelect: (clientData: ClientData) => void;
    svcCatId: number;
    userId: number;
    parentCorpId?: number | string;
}

const ClientSelection: React.FC<ClientSelectionProps> = ({
    serviceName,
    onBack,
    onClientSelect,
    svcCatId,
    userId,
    parentCorpId
}) => {
    const [clients, setClients] = useState<ClientData[]>([]);
    const [filteredClients, setFilteredClients] = useState<ClientData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    useEffect(() => {
        fetchClients();
    }, [svcCatId, userId, parentCorpId]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && selectedClient) {
                handleProceed();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedClient]);

    const fetchClients = async () => {
        setLoading(true);
        try {
            const response = await getClients(svcCatId.toString(), userId.toString(), parentCorpId);
            
            if (response?.data) {
                const clientsData = Array.isArray(response.data) ? response.data : [];
                setClients(clientsData);
                setFilteredClients(clientsData);
            } else {
                setError('No clients found');
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
            setError('Failed to fetch clients');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchQuery(value);
        const filtered = clients.filter(client =>
            client.clt_corporation_details?.full_name?.toLowerCase().includes(value.toLowerCase()) ||
            client.clt_legal_entity_details?.name?.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredClients(filtered);
    };

    const handleClientSelect = (client: ClientData) => {
        setSelectedClient(client);
    };

    const handleProceed = () => {
        if (selectedClient) {
            setIsLoggingIn(true);
            setTimeout(() => {
                onClientSelect(selectedClient);
            }, 500);
        }
    };

    if (isLoggingIn && selectedClient) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-2xl p-8">
                <div className="text-center mb-8">
                    <p className="text-[#101828] text-lg mb-6">
                        Logging in to {serviceName}.
                    </p>
                    <div className="w-16 h-16 mx-auto mb-4">
                        <Avatar
                            src={selectedClient.clt_corporation_details.logo_url || '/assets/login/company-default.svg'}
                            size={64}
                            className="bg-[#F4EBFF]"
                            style={{ border: '1px solid #F9F5FF' }}
                        />
                    </div>
                    <p className="text-[#101828] text-base font-medium">
                        {selectedClient.clt_corporation_details.full_name}
                    </p>
                </div>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    return (
        <div className="w-full bg-white rounded-2xl shadow-sm flex flex-col h-[600px]">
            <div className="px-6 pt-6 pb-4">
                {/* Back button and Title */}
                <div className="flex items-center gap-3 mb-6">
                    <button 
                        onClick={onBack}
                        className="p-2 hover:bg-[#F9FAFB] rounded-lg transition-colors"
                    >
                        <ArrowLeftOutlined className="text-[#667085]" />
                    </button>
                    <div>
                        <h1 className="text-[28px] font-semibold text-[#101828] mb-1">
                            Workspaces : {serviceName}
                        </h1>
                        <p className="text-base text-[#475467]">
                            {filteredClients.length} {filteredClients.length === 1 ? 'client' : 'clients'}
                        </p>
                    </div>
                </div>

                {/* Search */}
                <div className="mb-4">
                    <Input
                        prefix={<SearchOutlined className="text-[#667085]" />}
                        placeholder="Search"
                        onChange={(e) => handleSearch(e.target.value)}
                        value={searchQuery}
                        className="w-full py-2.5 px-3.5 rounded-lg border border-[#D0D5DD]"
                    />
                </div>
            </div>

            {/* Client List - Made Scrollable */}
            <div className="px-4 space-y-1 flex-1 overflow-y-auto">
                {filteredClients.map((client) => (
                    <div
                        key={`${client.clt_corporation_details?.id}-${client.clt_legal_entity_details?.id}`}
                        className={`group cursor-pointer rounded-lg transition-all duration-200 hover:bg-[#F9FAFB] ${
                            selectedClient?.clt_corporation_details.id === client.clt_corporation_details.id
                                ? 'bg-[#F9FAFB]'
                                : ''
                        }`}
                        onClick={() => handleClientSelect(client)}
                    >
                        <div className="flex items-center justify-between py-2.5 px-2">
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                    <Avatar
                                        src={client.clt_corporation_details.logo_url || '/assets/login/company-default.svg'}
                                        size={32}
                                        className="bg-[#F4EBFF]"
                                        style={{ border: '1px solid #F9F5FF' }}
                                    />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-[#101828]">
                                        {client.clt_corporation_details.full_name}
                                    </p>
                                    {/* <p className="text-xs text-[#667085]">
                                        {client?.clt_legal_entity_details?.name}
                                    </p> */}
                                </div>
                            </div>
                            <RightOutlined className="text-[#667085]" />
                        </div>
                    </div>
                ))}
            </div>

            {/* Proceed Button - Made Sticky */}
            <div className="p-4 border-t border-[#EAECF0] bg-white sticky bottom-0">
                <button
                    onClick={handleProceed}
                    disabled={!selectedClient}
                    className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors ${
                        selectedClient
                            ? 'bg-[#7F56D9] text-white hover:bg-[#6941C6]'
                            : 'bg-[#F2F4F7] text-[#D0D5DD] cursor-not-allowed'
                    }`}
                >
                    Proceed to login
                </button>
            </div>
        </div>
    );
};

export default ClientSelection; 